/**
 * RoleLandingRouter Component
 *
 * Routes users to their role-specific landing page based on their effective role.
 * Uses the effectiveRole from RoleAssumptionContext to respect role assumption.
 */

import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { useAuth } from '../../context/AuthContext';
import {
  PlatformDashboard,
  CRAAdminDashboard,
  AnalystWorkbench,
  CommitteeDashboard,
} from '../../pages/dashboards';
import InstitutionDashboard from '../../pages/InstitutionDashboard';

/**
 * DefaultLanding - Fallback for users without a role (pending approval)
 */
function DefaultLanding() {
  const { userProfile } = useAuth();

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Welcome to BankingLATAM</h1>
        <p className="dashboard-subtitle">
          {userProfile?.status === 'pending'
            ? 'Your account is pending approval. Please wait for an administrator to review your request.'
            : 'Your account does not have an assigned role. Please contact an administrator.'}
        </p>
      </div>
      <div className="dashboard-content">
        <div className="dashboard-card">
          <div className="card-icon">⏳</div>
          <h3>Account Status</h3>
          <p>Status: <strong>{userProfile?.status || 'Unknown'}</strong></p>
          <p>Role: <strong>{userProfile?.role || 'Not assigned'}</strong></p>
        </div>
      </div>
    </div>
  );
}

/**
 * RoleLandingRouter - Routes to appropriate dashboard based on effective role
 *
 * Bank users (bank_admin, bank_user) now see the new InstitutionDashboard
 * which automatically loads their assigned institution's data.
 */
export default function RoleLandingRouter() {
  const { effectiveRole } = useRoleAssumption();

  switch (effectiveRole) {
    case 'platform_admin':
      return <PlatformDashboard />;
    case 'cra_admin':
      return <CRAAdminDashboard />;
    case 'cra_analyst':
      return <AnalystWorkbench />;
    case 'rating_committee':
      return <CommitteeDashboard />;
    case 'bank_admin':
    case 'bank_user':
      // Bank users see the new Institution Dashboard with their assigned institution
      return <InstitutionDashboard />;
    default:
      return <DefaultLanding />;
  }
}
